/**
 * IndexedDB persistence queue for Shorts background uploads.
 * Preserves pending video blobs and metadata across page reloads and app restarts.
 */

const DB_NAME = "kekkle-short-uploads";
const DB_VERSION = 1;
const STORE_NAME = "uploads";

export type ShortUploadStatus = "uploading" | "failed" | "completed";

export type PersistedShortUpload = {
  id: string;
  groupId: string;
  authUuid: string;
  caption: string;
  duration: number;
  blurhash?: string;
  videoBlob: Blob;
  posterBlob?: Blob;
  createdAt: number;
  status: ShortUploadStatus;
  errorMessage?: string;
};

let dbPromise: Promise<IDBDatabase> | null = null;

function isIndexedDBSupported(): boolean {
  return typeof window !== "undefined" && typeof indexedDB !== "undefined";
}

/**
 * Initialize or retrieve the IndexedDB connection for short uploads
 */
function getDB(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  if (!isIndexedDBSupported()) {
    return Promise.reject(
      new Error("IndexedDB is not supported in this environment"),
    );
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };

    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("groupId", "groupId", { unique: false });
        store.createIndex("status", "status", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
  });

  return dbPromise;
}

/**
 * Saves or updates a short upload job in IndexedDB
 */
export async function saveUploadJob(job: PersistedShortUpload): Promise<void> {
  if (!isIndexedDBSupported()) return;
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(job);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Retrieves all stored short upload jobs
 */
export async function getAllUploadJobs(): Promise<PersistedShortUpload[]> {
  if (!isIndexedDBSupported()) return [];
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve((req.result as PersistedShortUpload[]) || []);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Retrieves a single short upload job by ID
 */
export async function getUploadJob(
  id: string,
): Promise<PersistedShortUpload | null> {
  if (!isIndexedDBSupported()) return null;
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(id);
    req.onsuccess = () => resolve((req.result as PersistedShortUpload) || null);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Updates the status and optional error message of an upload job
 */
export async function updateUploadJobStatus(
  id: string,
  status: ShortUploadStatus,
  errorMessage?: string,
): Promise<void> {
  if (!isIndexedDBSupported()) return;
  const job = await getUploadJob(id);
  if (!job) return;
  job.status = status;
  if (errorMessage !== undefined) {
    job.errorMessage = errorMessage;
  }
  await saveUploadJob(job);
}

/**
 * Deletes an upload job from IndexedDB
 */
export async function deleteUploadJob(id: string): Promise<void> {
  if (!isIndexedDBSupported()) return;
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Clears all short upload jobs (e.g. on user logout)
 */
export async function clearAllUploadJobs(): Promise<void> {
  if (!isIndexedDBSupported()) return;
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
